// ds-typo-ignore-file — pixel-accurate Figma-replica overlay text inside
// the benefits grid tiles (mini biomarker chart, cadence plan card,
// action plan rows, member-pricing rows, lab-upload mock UI). The 8–12px
// micro-typography is intentional to read at the displayed mockup scale
// and not meant to map to the marketing-site type tokens.
"use client"

/**
 * "What's included" / benefits grid — the 6-card image grid from the
 * membership page (Figma 193:2060 onward), extracted so the ambassadors
 * page can reuse it 1:1. The only knob is `disableLinks`: when true,
 * every card renders as a static <div> regardless of `item.anchor`, so
 * the grid can ship on pages that don't host the scroll targets.
 *
 * The component still reads its copy from the `_membership` translation
 * namespace; that file ships on every locale, so nothing extra needs
 * wiring on a host page.
 */

import { useEffect, useState } from "react"
import Image from "next/image"
import { useTranslations } from "@/lib/intl/use-translations"

import { useCountry } from "@/components/layout/country-provider"
import {
  AddTestsIcon,
  ExerciseIcon,
  NutritionIcon,
  Report2Icon,
} from "@/components/ui/icons"
import { HeartOrganIcon } from "@/components/ui/organ-icons"
import { blobMediaUrl } from "@/lib/blob-url"
import { cn } from "@/lib/utils"
import { membershipKeys as mk } from "@/i18n/membership-keys"
import { formatNumber } from "@/lib/format"
import { fetchPrices, type PriceData } from "@/lib/pricing"
import { liveRegularAndMember } from "@/lib/pricing/member-prices"
import { BiomarkerOverlay } from "./biomarker-overlay"

function scrollToId(id: string) {
  if (typeof document === "undefined") return
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: "smooth", block: "start" })
}

export const FIGMA_GRID_ITEMS: {
  labelKey: string
  image: string
  anchor?: string
  overlay?:
    | "biomarkers"
    | "cadence"
    | "action-plan"
    | "member-pricing"
    | "lab-upload"
}[] = [
  {
    labelKey: mk.includedItem1,
    image: blobMediaUrl("/images/membership/included-biomarkers.png"),
    anchor: "what-we-test",
    overlay: "biomarkers",
  },
  {
    labelKey: mk.includedItem2,
    image: blobMediaUrl("/images/membership/included-cadence.jpg"),
    anchor: "cadence",
  },
  {
    labelKey: mk.includedItem3,
    image: blobMediaUrl("/images/membership/included-action-plan.jpg"),
    anchor: "action-plan",
  },
  {
    labelKey: mk.includedItem4,
    image: blobMediaUrl("/images/membership/included-member-pricing.jpg"),
    anchor: "member-pricing",
    overlay: "member-pricing",
  },
  {
    labelKey: mk.includedItem5,
    image: blobMediaUrl("/images/membership/included-health-support.jpg"),
  },
  {
    labelKey: mk.includedItem6,
    image: blobMediaUrl("/images/membership/included-upload-lab.jpg"),
    anchor: "what-we-test",
    overlay: "lab-upload",
  },
]

export function FigmaGridCard({
  item,
  delay: _delay,
  disableLinks = false,
  prices = null,
}: {
  item: (typeof FIGMA_GRID_ITEMS)[number]
  delay?: number
  disableLinks?: boolean
  /** Live Stripe prices for the member-pricing tile. */
  prices?: PriceData | null
}) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const interactive = !disableLinks && Boolean(item.anchor)
  const inner = (
    <>
      <div className="relative aspect-[8/7] overflow-hidden rounded-3xl bg-neutral-200">
        <Image
          src={item.image}
          alt=""
          fill
          sizes="(max-width: 768px) 100vw, 25vw"
          className={cn(
            "object-cover transition-transform duration-500",
            interactive && "group-hover:scale-[1.03]"
          )}
        />
        {item.overlay === "biomarkers" && <BiomarkerOverlay />}
        {item.overlay === "cadence" && <CadenceOverlay />}
        {item.overlay === "action-plan" && <ActionPlanOverlay />}
        {item.overlay === "member-pricing" && (
          <MemberPricingOverlay prices={prices} />
        )}
        {item.overlay === "lab-upload" && (
          <div className="hidden md:contents">
            <LabUploadOverlay />
          </div>
        )}
      </div>
      <p
        className={cn(
          "type-body-md md:type-body-lg font-medium! text-neutral-900 opacity-80 transition-opacity",
          interactive && "group-hover:opacity-100"
        )}
      >
        {t(item.labelKey)}
      </p>
    </>
  )
  return (
    <div>
      {interactive ? (
        <button
          type="button"
          onClick={() => scrollToId(item.anchor as string)}
          className="group flex w-full cursor-pointer flex-col gap-2 text-left md:gap-4"
        >
          {inner}
        </button>
      ) : (
        <div className="flex w-full flex-col gap-2 text-left md:gap-4">
          {inner}
        </div>
      )}
    </div>
  )
}

/**
 * Stable identifiers for the 6 default tiles. Use these with
 * `excludeKeys` instead of hard-coding membership-key strings into
 * consumer pages so the mapping stays in this file.
 */
export const INCLUDED_KEYS = {
  biomarkers: mk.includedItem1,
  cadence: mk.includedItem2,
  actionPlan: mk.includedItem3,
  memberPricing: mk.includedItem4,
  healthSupport: mk.includedItem5,
  uploadLab: mk.includedItem6,
} as const

export function IncludedFigmaGrid({
  disableLinks = false,
  excludeKeys,
  heading,
  eyebrow,
  prices: serverPrices,
}: {
  /** Live Stripe prices (get-products) for the member-pricing tile. Omitted →
   *  fetched client-side. Without a live regular + member pair the tile shows
   *  no figures (AHD-13080). */
  prices?: PriceData | null
  disableLinks?: boolean
  /** labelKey values (use `INCLUDED_KEYS.<name>`) to filter out. */
  excludeKeys?: readonly string[]
  /** Override the default heading ("One membership…"). */
  heading?: string
  /** Override the default eyebrow ("What's included"). */
  eyebrow?: string
} = {}) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const { code: country } = useCountry()
  const [prices, setPrices] = useState<PriceData | null>(serverPrices ?? null)
  useEffect(() => {
    if (serverPrices !== undefined) {
      setPrices(serverPrices)
      return
    }
    fetchPrices(country).then(setPrices)
  }, [country, serverPrices])
  const items = excludeKeys?.length
    ? FIGMA_GRID_ITEMS.filter((it) => !excludeKeys.includes(it.labelKey))
    : FIGMA_GRID_ITEMS
  const resolvedHeading = heading ?? t(mk.includedHeading)
  const resolvedEyebrow = eyebrow ?? t(mk.includedLabel)

  // Default 6-item layout interleaves the heading as a col-span-2 cell
  // alongside the first two tiles. With fewer tiles (e.g. /ambassadors —
  // 4 tiles after biomarkers/action-plan/member-pricing/health-support)
  // the interleaved heading leaves awkward empty cells, so promote the
  // heading to its own full-width row and let the remaining tiles fill
  // a clean 4-col row below.
  const compactLayout = items.length <= 4

  if (compactLayout) {
    return (
      <div className="flex flex-col gap-8 md:gap-12">
        <div className="flex flex-col gap-2 md:gap-4">
          <p className="type-label-md text-neutral-500">{resolvedEyebrow}</p>
          <h2 className="type-display-sm max-w-3xl text-neutral-900">
            {resolvedHeading}
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-5 md:grid-cols-4 md:gap-6">
          {items.map((item, i) => (
            <FigmaGridCard
              key={item.labelKey}
              item={item}
              delay={0.04 + i * 0.04}
              disableLinks={disableLinks}
              prices={prices}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-5 md:grid-cols-4 md:gap-6">
      <div className="col-span-2 flex flex-col justify-start gap-2 pr-4 md:gap-4">
        <p className="type-label-md text-neutral-500">{resolvedEyebrow}</p>
        <h2 className="type-display-sm text-neutral-900">{resolvedHeading}</h2>
      </div>
      {items.map((item, i) => (
        <FigmaGridCard
          key={item.labelKey}
          item={item}
          delay={0.04 + i * 0.04}
          disableLinks={disableLinks}
          prices={prices}
        />
      ))}
    </div>
  )
}

/* Figma 193:2068 — floating "Plan details" card with three cadence options. */
function CadenceOverlay() {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const plans = [
    {
      name: t(mk.overlayCadenceBaselineName),
      desc: t(mk.overlayCadenceBaselineDesc),
      state: "unselected" as const,
    },
    {
      name: t(mk.overlayCadence2xName),
      desc: t(mk.overlayCadence2xDesc),
      state: "selected" as const,
    },
    {
      name: t(mk.overlayCadence4xName),
      desc: t(mk.overlayCadence4xDesc),
      state: "dim" as const,
    },
  ]
  return (
    <div className="pointer-events-none absolute top-1/2 left-1/2 flex w-[86%] -translate-x-1/2 -translate-y-1/2 flex-col gap-[8px] rounded-[10px] bg-[rgba(36,36,36,0.3)] p-[8px] backdrop-blur-[6.905px]">
      {" "}
      {/* ds-color-ignore: translucent overlay */}
      <div className="flex flex-col gap-[4px]">
        {plans.map((p) => (
          <div
            key={p.name}
            className="flex items-center gap-[7px] rounded-[8px] bg-[rgba(17,17,17,0.36)] px-[7px] py-[6px]" // ds-color-ignore: translucent overlay
          >
            <CadenceRadio state={p.state} />
            <div className="flex min-w-0 flex-1 flex-col gap-[1px]">
              <p className="text-[9px] leading-[12px] font-medium tracking-[0.05px] whitespace-nowrap text-white/65">
                {p.name}
              </p>
              <p className="truncate text-[8px] leading-[10px] font-normal text-white/40">
                {p.desc}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function CadenceRadio({ state }: { state: "selected" | "unselected" | "dim" }) {
  const opacity = state === "dim" ? "opacity-30" : "opacity-100"
  return (
    <div className={cn("shrink-0", opacity)}>
      {state === "selected" ? (
        <svg
          width="11"
          height="11"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="8"
            cy="8"
            r="7"
            stroke="white"
            strokeWidth="1.25"
            fill="none"
          />
          <path
            d="M5 8l2 2 4-4"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      ) : (
        <svg
          width="11"
          height="11"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="8"
            cy="8"
            r="7"
            stroke="white"
            strokeWidth="1.25"
            fill="none"
          />
        </svg>
      )}
    </div>
  )
}

/* Figma 195:4817 / 194:3301 — action plan card. Reused both as the grid
   overlay (compact) and as a larger standalone card on the membership page
   (lg). */
export function ActionPlanCard({
  className,
  size = "compact",
}: {
  className?: string
  size?: "compact" | "lg"
}) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const items = [
    {
      Icon: NutritionIcon,
      title: t(mk.overlayActionCalciumTitle),
      desc: t(mk.overlayActionCalciumDesc),
    },
    {
      Icon: ExerciseIcon,
      title: t(mk.overlayActionExerciseTitle),
      desc: t(mk.overlayActionExerciseDesc),
    },
    {
      Icon: Report2Icon,
      title: t(mk.overlayActionPotassiumTitle),
      desc: t(mk.overlayActionPotassiumDesc),
    },
  ]
  const isLg = size === "lg"
  return (
    <div
      className={cn(
        "pointer-events-none flex flex-col",
        isLg
          ? "gap-[6px] rounded-[15.065px] p-0"
          : "gap-[6px] rounded-[20px] border-[0.4px] border-white/20 bg-white/20 p-[12px] shadow-none backdrop-blur-[6px]",
        className
      )}
    >
      {items.map(({ Icon, title, desc }) => (
        <div
          key={title}
          className={cn(
            "flex items-center rounded-[10px] bg-neutral-950",
            isLg
              ? "items-start gap-[10.043px] rounded-[10.043px] px-[10.043px] py-[12px]"
              : "gap-[10px] px-[10px] py-[8px]"
          )}
        >
          <div
            className={cn(
              "flex shrink-0 items-center justify-center",
              isLg ? "w-[32px] self-stretch" : "w-[20px]"
            )}
          >
            <Icon size={isLg ? 20 : 18} className="text-white" />
          </div>
          <div
            className={cn(
              "flex min-w-0 flex-1 flex-col",
              isLg ? "gap-[2.511px]" : "gap-[1px]"
            )}
          >
            <p
              className={cn(
                "tracking-[-0.2px] whitespace-nowrap",
                isLg
                  ? "text-[12px] leading-[15.065px] font-medium text-white/80"
                  : "text-[12px] leading-[15px] font-semibold text-white"
              )}
            >
              {title}
            </p>
            <p
              className={cn(
                "truncate",
                isLg
                  ? "text-[10px] leading-[12.554px] font-normal text-white/50"
                  : "text-[11px] leading-[14px] tracking-[-0.2px] text-white/80"
              )}
            >
              {desc}
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}

function ActionPlanOverlay() {
  return (
    <ActionPlanCard className="absolute top-1/2 left-1/2 w-[86%] -translate-x-1/2 -translate-y-1/2" />
  )
}

/** A tile row's "regular → member" pair from the live feed, formatted for the
 *  market (e.g. "1'990"), or null when the feed lacks either figure. */
function tileFigures(
  prices: PriceData | null,
  key: string,
  country: string
): { regular: string; member: string } | null {
  const live = liveRegularAndMember(prices?.prices, key)
  return live
    ? {
        regular: formatNumber(live.regular, country),
        member: formatNumber(live.member, country),
      }
    : null
}

function MemberPricingOverlay({ prices }: { prices: PriceData | null }) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const { code: country } = useCountry()
  return (
    <div className="pointer-events-none absolute top-1/2 left-1/2 flex w-[86%] -translate-x-1/2 -translate-y-1/2 flex-col gap-[5px]">
      <p className="text-foreground text-[8px] leading-[12px] font-medium tracking-[0.08px]">
        {t(mk.overlayMemberPricingHeading)}
      </p>
      <MemberPricingRow
        thumb={
          <div className="bg-light-green-500 flex size-[30px] shrink-0 items-center justify-center rounded-[11px]">
            <AddTestsIcon size={22} className="text-white" />
          </div>
        }
        title={t(mk.overlayMemberPricingMri)}
        figures={tileFigures(prices, "core", country)}
      />
      <MemberPricingRow
        thumb={
          <div className="bg-error-300 relative size-[30px] shrink-0 overflow-hidden rounded-[8px]">
            <HeartOrganIcon
              size={24}
              className="absolute inset-0 m-auto text-[#7c2a1c]"
            />
          </div>
        }
        title={t(mk.overlayMemberPricingHeartCt)}
        figures={tileFigures(prices, "addonHeartCT", country)}
      />
    </div>
  )
}

function MemberPricingRow({
  thumb,
  title,
  figures,
}: {
  thumb: React.ReactNode
  title: string
  /** Live regular/member pair; null hides the figures, never a literal. */
  figures: { regular: string; member: string } | null
}) {
  const { currency } = useCountry()

  return (
    <div className="flex items-center gap-[7px] rounded-[10px] border-[0.4px] border-white/40 bg-white/55 p-[6px] backdrop-blur-[6px]">
      {thumb}
      <div className="flex min-w-0 flex-1 flex-col gap-[1px]">
        <p className="text-foreground truncate text-[10px] leading-[13px] font-medium">
          {title}
        </p>
        <p className="flex items-baseline gap-[3px] text-[9px] leading-[12px] whitespace-nowrap text-neutral-600">
          {currency === "CHF" && figures ? (
            <>
              <span>{currency}</span>
              <span className="line-through">{figures.regular}</span>
              <span className="text-foreground font-bold tracking-[0.05px]">
                {figures.member}
              </span>
            </>
          ) : currency !== "CHF" ? (
            <span className="text-foreground font-bold tracking-[0.05px]">
              {currency}
            </span>
          ) : null}
        </p>
      </div>
      <div className="text-foreground/70 flex shrink-0 items-center justify-center">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="M8 12h8M12 8v8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </div>
    </div>
  )
}

function LabUploadOverlay() {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  return (
    <div className="pointer-events-none absolute top-1/2 left-1/2 flex w-[86%] -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-[9px] overflow-hidden rounded-[14px] bg-white/[0.07] px-[10px] pt-[9px] pb-[11px] backdrop-blur-[6px]">
      <p className="max-w-full text-center text-[10px] leading-[13px] font-medium tracking-[0.1px] text-white/80">
        {t(mk.overlayLabUploaded)}
      </p>
      <div className="flex w-full items-center justify-between gap-[8px] rounded-[9px] bg-white/[0.31] p-[7px]">
        <div className="flex flex-col gap-[4px]">
          <div className="h-[12px] w-[18px] rounded-[3px] bg-white/[0.34]" />
          <div className="h-[3px] w-[48px] rounded-[3px] bg-white/[0.34]" />
          <div className="from-light-green-200 to-terracotta-100 inline-flex w-fit items-center rounded-full bg-gradient-to-r px-[4px] py-[1px]">
            <span className="text-light-green-700 text-[6px] leading-none font-medium whitespace-nowrap">
              {t(mk.overlayWithinRange)}
            </span>
          </div>
        </div>
        <div className="relative h-[32px] w-[60px] shrink-0">
          <div className="absolute top-0 right-0 flex h-full w-[3px] flex-col gap-[1.5px]">
            <div className="bg-terracotta-300 flex-1 rounded-t-[2px]" />
            <div className="bg-sand-400 flex-1" />
            <div className="bg-light-green-400 flex-1 rounded-b-[2px]" />
          </div>
          <div className="absolute top-[30%] left-[40%] size-[5px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/80" />
          <div className="absolute top-[72%] left-[72%] size-[6px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#7b8c3b] shadow-[0_0_0_1.25px_rgba(255,255,255,0.95)]" />
        </div>
      </div>
    </div>
  )
}
